import { useState, useEffect } from "react";
import MaskIcon from "common_components/ui/mask_icon/mask_icon.ui";
import { Assets, Slider, ToggleIconButton } from "utils/imports.utils";
import { ObjectTypes, PropertiesTypes } from "utils/interface.utils";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { TARGET_IMAGE_DIMENSIONS_CANVAS } from "utils/constant.utils";
import { STRINGS } from "constants/strings.constant";
import "./transform_properties.component.scss";

const strings = STRINGS.editor.properties;

const POSITION_RANGE = { min: -1500, max: 1500, step: 1 };
const ROTATION_RANGE = { min: -360, max: 360, step: 1 };

interface UnitInputProps {
    label: string;
    unit: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    disabled: boolean;
    prefix?: string;
}

const UnitInput = ({ label, unit, value, onChange, min, max, step, disabled, prefix }: UnitInputProps) => (
    <label className="transform_controls_field">
        {prefix && <span className="transform_controls_field_prefix">{prefix}</span>}
        <input
            className="transform_controls_field_input"
            type="number"
            aria-label={label}
            value={value}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onChange={(e) => onChange(+e.target.value)}
        />
        <span className="transform_controls_field_unit">{unit}</span>
    </label>
);

interface SliderRowProps extends Omit<UnitInputProps, "prefix"> {
    title: string;
    sliderValue: number;
    sliderMin: number;
    sliderMax: number;
    sliderStep: number;
    onSliderChange: (value: number) => void;
}

const SliderRow = ({ title, sliderValue, sliderMin, sliderMax, sliderStep, onSliderChange, ...inputProps }: SliderRowProps) => (
    <div className="transform_controls_group">
        <div className="transform_controls_group_title">{title}</div>
        <div className="transform_controls_slider_row">
            <div className="transform_controls_slider">
                <Slider
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                    value={[sliderMin, sliderValue]}
                    onInput={(values: [number, number]) => onSliderChange(values[1])}
                    isRange={false}
                    disabled={inputProps.disabled}
                />
            </div>
            <UnitInput {...inputProps} />
        </div>
    </div>
);

interface TransformPropertiesProps {
    state: Record<string, Record<string, unknown>>;
    setState: (update: Record<string, Record<string, unknown>>) => void;
    currentSelectedPropertyType: string;
    maxScale: number;
}

const num = (value: unknown): number => (typeof value === "number" ? value : 0);

const TransformProperties = ({ state, setState, currentSelectedPropertyType, maxScale }: TransformPropertiesProps) => {
    const [isKeepProportions, setIsKeepProportions] = useState<boolean>(true);
    const { selectedObjectID, targetImageDataList, selectedTargetIndex } = useSelector((root: RootState) => root.editor);

    const currentObject = targetImageDataList[selectedTargetIndex ?? 0]?.objects.find((object) => object.id === selectedObjectID);
    const current = state[currentSelectedPropertyType] ?? {};
    const hasScaleZ = current.scaleZ !== undefined;
    const isSocial = currentSelectedPropertyType === PropertiesTypes.SOCIAL;
    const hasScale = !(currentSelectedPropertyType === PropertiesTypes.TEXT || currentSelectedPropertyType === PropertiesTypes.BUTTON);
    const isMediaWithoutSrc = (currentObject?.type === ObjectTypes.IMAGE || currentObject?.type === ObjectTypes.VIDEO) && currentObject?.properties?.src === null;
    const isDisabled = currentSelectedPropertyType === PropertiesTypes.TARGET || isMediaWithoutSrc;

    // Social icons use scaleX/scaleY as their circle's diameter, so the same ceiling would let one
    // grow to ~4x the whole target image's width.
    const scaleRange = { min: 1, max: isSocial ? maxScale * 0.2 : maxScale * 4, step: 0.1 };

    useEffect(() => {
        setIsKeepProportions(true);
    }, [selectedObjectID]);

    const update = (changes: Record<string, unknown>) => {
        setState({ [currentSelectedPropertyType]: { ...current, ...changes } });
    };

    const changeScale = (prop: "scaleX" | "scaleY", value: number) => {
        if (!isKeepProportions) return update({ [prop]: value });
        const difference = value - num(current[prop]);
        update({
            scaleX: difference + num(current.scaleX),
            scaleY: difference + num(current.scaleY),
            ...(hasScaleZ ? { scaleZ: difference + num(current.scaleZ) } : {}),
        });
    };

    // Scale returns to the size the object started at: images and videos record it when the file is
    // uploaded (defaultScaleX/Y), a social icon starts at a fixed size, text and buttons at 1.
    const fallbackScale = isSocial ? TARGET_IMAGE_DIMENSIONS_CANVAS * 0.05 : 1;
    const reset = () => update({
        positionX: 0,
        positionY: 0,
        rotationX: 0,
        scaleX: typeof current.defaultScaleX === "number" ? current.defaultScaleX : fallbackScale,
        scaleY: typeof current.defaultScaleY === "number" ? current.defaultScaleY : fallbackScale,
        ...(hasScaleZ ? { scaleZ: 1 } : {}),
    });

    // Scale is the object's size in canvas pixels (an uploaded 300 x 200 image reads 300 and 200).
    const roundSize = (scale: number) => Math.round(scale * 10) / 10;

    const scaleField = (prop: "scaleX" | "scaleY", label: string, prefix: string) => (
        <UnitInput
            label={label}
            prefix={prefix}
            unit={strings.unitPx}
            value={roundSize(num(current[prop]))}
            onChange={(size) => changeScale(prop, size)}
            min={scaleRange.min}
            max={scaleRange.max}
            step={scaleRange.step}
            disabled={isDisabled}
        />
    );

    return (
        <div className="transform_controls">
            <div className="transform_controls_header">
                <h3 className="transform_controls_heading">{strings.transform}</h3>
                <button type="button" className="transform_controls_reset" onClick={reset} disabled={isDisabled}>
                    <MaskIcon src={Assets.reset} size={16} />
                    {strings.reset}
                </button>
            </div>

            <div className="transform_controls_group">
                <div className="transform_controls_group_title">{strings.position}</div>
                <div className="transform_controls_position_row">
                    {(["X", "Y"] as const).map((axis) => (
                        <UnitInput
                            key={axis}
                            label={`${strings.position} ${axis}`}
                            prefix={axis}
                            unit={strings.unitPx}
                            value={num(current[`position${axis}`])}
                            onChange={(value) => update({ [`position${axis}`]: value })}
                            {...POSITION_RANGE}
                            disabled={isDisabled}
                        />
                    ))}
                </div>
            </div>

            <SliderRow
                title={strings.rotation}
                label={strings.rotation}
                unit={strings.unitDegree}
                value={num(current.rotationX)}
                onChange={(value) => update({ rotationX: value })}
                {...ROTATION_RANGE}
                sliderValue={num(current.rotationX)}
                sliderMin={ROTATION_RANGE.min}
                sliderMax={ROTATION_RANGE.max}
                sliderStep={ROTATION_RANGE.step}
                onSliderChange={(value) => update({ rotationX: value })}
                disabled={isDisabled}
            />

            {hasScale && (
                <div className="transform_controls_group">
                    <div className="transform_controls_group_title">{strings.scale}</div>
                    <div className="transform_controls_scale_row">
                        {scaleField("scaleX", strings.width, strings.widthShort)}
                        <ToggleIconButton
                            icon={Assets.link_chain}
                            label={strings.keepProportions}
                            isPressed={isKeepProportions}
                            onToggle={() => setIsKeepProportions(!isKeepProportions)}
                            disabled={isDisabled}
                        />
                        {scaleField("scaleY", strings.height, strings.heightShort)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransformProperties;
